import React, { useEffect, useRef } from 'react';

import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import clsx from 'clsx';
import cookie from 'cookie_js';
import { useForm } from 'react-hook-form';
import { hashHistory } from 'react-router';

import { getUserMessage } from '../../../core/api/apiUtils';
import { postRequestPhoneLoginCodeSms } from '../../../core/api/user/user.service';
import { AppConfig } from '../../../core/api/whitelabel/whitelabel.types';
import { analyticsService } from '../../../core/services';
import { logger } from '../../../core/services/loggerService';
import { HCaptchaTokens } from '../../../core/state/user/user.types';
import { AxiosError } from '../../../core/types/data.types';
import { Routes } from '../../../core/types/route.types';
import { Track } from '../../../weborder/analytics';
import * as Api from '../../../weborder/Api';
import { cookieConsent } from '../../../weborder/cookieconsent';
import {
  CUSTOM_EVENTS,
  publishCustomEvent,
} from '../../../weborder/CustomEvents';
import { Button, IconButton, TextField, Typography } from '../../atoms';
import { ArrowLeft as ArrowLeftIcon } from '../../icons';
import { Dialog } from '../../layouts';
import { PhoneCallButton } from './partials';
import { checkPhoneNumberLeadingCharacters } from './utils/loginConfirmDialogUtils';

const useStyles = makeStyles((theme: Theme) => ({
  loginConfirmDialog: {
    '& button': {
      display: 'block',
    },

    '& .fd-text__h1': {
      marginBottom: theme.spacing(0.5),
    },

    '& .fd-text__body1': {
      color: theme.palette.grey[800],
      marginBottom: theme.spacing(1),
    },

    '& .fd-text__body2-secondary': {
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.fontSize,
      },
    },
  },

  bottomMargin: {
    marginBottom: theme.spacing(3.125),
  },
}));

interface FormInput {
  smsCode: string;
}
interface CodePayload {
  deviceId: string;
  PhoneNumber: string;
  rememberMe?: boolean;
  selectedDeviceDeliveryLocationId: number | undefined;
  SmsCode: string;
}

interface Props {
  afterLoginUrl: string;
  allowGuestLogin: boolean;
  configData: AppConfig;
  deviceId: string;
  enableRememberMe: boolean;
  hasGdprCookieFeatureFlag: boolean;
  hCaptchaTokens: HCaptchaTokens;
  login: () => void;
  params: Routes['params'];
  rememberMe: boolean;
  setLoginErrorMessage: (message: string) => void;
  setRememberMe: (boolean) => void;
  translate: any;
}

export function LoginConfirmDialog(props: Props): JSX.Element {
  const {
    afterLoginUrl,
    allowGuestLogin,
    configData,
    deviceId,
    enableRememberMe,
    hasGdprCookieFeatureFlag,
    hCaptchaTokens,
    login,
    params,
    rememberMe,
    setLoginErrorMessage,
    setRememberMe,
    translate,
  } = props;

  const classes = useStyles();

  const isProcessingCode = useRef(false);
  const isCallRequested = useRef(false);
  const errorMessage = useRef('');
  const pageStartTime = useRef(performance.now());

  const { handleSubmit, register, reset } = useForm<FormInput>();

  const strippedPhoneNumber = checkPhoneNumberLeadingCharacters(params.phone);

  const smsCodeAriaLabel = `${translate('Your_code_was_sent_to')} ${
    params.phone
  }`;

  const getLoginAnalyticsData = (
    elapsedTime: number,
    label: string,
    successfull = false,
    userType = 'guest',
  ): Record<string, boolean | number | string | Record<string, string>> => ({
    amp_countryCode: 'n/a',
    amp_label: 'Entered OTP',
    amp_loginSuccessful: successfull,
    amp_timeToEnterOtpInSec: elapsedTime,
    amp_userProperties: {
      userType,
    },
    label,
    section: 'Login',
  });

  useEffect(() => {
    const sendPhoneCode = (): Promise<void> =>
      postRequestPhoneLoginCodeSms(
        {
          deviceName: window.navigator.userAgent,
          phoneNumber: strippedPhoneNumber,
        },
        hCaptchaTokens.requestPhoneLoginCodeSmsToken,
      )
        .then(() => {
          analyticsService.track({
            amp_label: 'Phone number submitted',
            amp_phoneNumberSubmittedSuccessfully: true,
          });
        })
        .catch((error: AxiosError) => {
          const message = getUserMessage(error, translate);

          analyticsService.track({
            amp_label: 'Phone number submitted',
            amp_phoneNumberSubmittedSuccessfully: false,
          });
          logger.error('API Request Error - Login RequestPhoneLoginCodeSms', {
            ...error,
            message,
            hCaptchaToken: hCaptchaTokens.requestPhoneLoginCodeSmsToken,
          });

          handleBackLink(message);
        });

    const isHCaptchaTokenEmpty =
      process.env.REACT_APP_HCAPTCHA_SITE_KEY &&
      hCaptchaTokens.requestPhoneLoginCodeSmsToken === '';
    if (isHCaptchaTokenEmpty) {
      handleBackLink();
    } else {
      sendPhoneCode();
    }
  }, []);

  const getElapsedTimeForAnalytics = (pageUseEndTime): number =>
    Math.round((pageUseEndTime - pageStartTime.current) / 1000);

  const setAuthCookie = (flipdishToken: string): any => {
    if (!flipdishToken) return;

    const sixMonthsDate = new Date();
    sixMonthsDate.setMonth(sixMonthsDate.getMonth() + 6); // Expires in 6 months

    cookie.set('flipdish-token', flipdishToken, {
      expires: sixMonthsDate,
      path: '/',
      secure: true,
      // We need to do this for datadog browser tests as they are in iframe.
      sameSite: configData.WhiteLabelId === 13544 ? 'none' : 'strict',
    });
  };

  const applyFlipdishToken = (flipdishToken) => {
    if (flipdishToken) {
      setAuthCookie(flipdishToken);
      publishCustomEvent({ name: CUSTOM_EVENTS.USER_LOGIN });
      axios.defaults.headers.common['X-Flipdish-Token'] = flipdishToken;
    }
  };

  const onSubmit = (data: FormInput) => {
    const selectedDeviceDeliveryLocationId = Number(params.dlid);

    isProcessingCode.current = true;
    errorMessage.current = '';

    let payload: CodePayload = {
      deviceId,
      PhoneNumber: strippedPhoneNumber,
      selectedDeviceDeliveryLocationId,
      SmsCode: data.smsCode,
    };

    if (hasGdprCookieFeatureFlag && enableRememberMe) {
      payload = { ...payload, rememberMe };
    }

    axios
      .post('/Account/LoginUsingPhoneNumber/', payload, {
        headers: { 'Suppress-Auth-Cookie': 'true' },
      })
      .then((response) => {
        applyFlipdishToken(response.headers['x-flipdish-token']);

        if (enableRememberMe && rememberMe) {
          const selectedCookies = cookieConsent.getConsentCookies();
          const hasFunctionalCookie =
            selectedCookies && selectedCookies.some((c) => c === 'functional');

          if (!hasFunctionalCookie) {
            selectedCookies.push('functional');
            cookieConsent.setConsentCookies(selectedCookies);
          }
          setRememberMe(false);
        }

        const result: Api.Result<any> = response.data;

        const pageUseEndTime = performance.now();
        const elapsedTime = getElapsedTimeForAnalytics(pageUseEndTime);

        if (result.Success) {
          login();
          hashHistory.replace(afterLoginUrl);
          Track(
            'Login',
            'SMS Code Entry Successful',
            getLoginAnalyticsData(
              elapsedTime,
              'SMS Code Entry Successful',
              result.Success,
              'user',
            ),
          );
        } else {
          isProcessingCode.current = false;
          errorMessage.current = translate(
            'LoginConfirm_LoginConfirmDialog_InvalidLogin',
          );

          reset();

          Track(
            'Login',
            'SMS Code Entry Failed',
            getLoginAnalyticsData(
              elapsedTime,
              'SMS Code Entry Failed',
              result.Success,
              'user',
            ),
          );
        }
      })
      .catch((_error) => {
        Track('Login', 'SMS Code Entry Failed');
      });

    Track('Login', 'Submit SMS Code Button');
  };

  const handleSmsNumberChange = (event: any) => {
    if (event.target.value.length >= 4) {
      handleSubmit(onSubmit)();
    }
  };

  const handleBackLink = (loginErrorMessage?: string): void => {
    const backlinkPath = params.dlid ? `/phone/${params.dlid}` : '/phone';

    if (loginErrorMessage !== undefined) {
      setLoginErrorMessage(loginErrorMessage);
    }

    hashHistory.replace(backlinkPath);
  };

  const handleGuestLogin = () => {
    axios
      .post(`/Account/guestUserLogin?wlid=${configData.AppNameId}`)
      .then((response) => {
        applyFlipdishToken(response.headers['x-flipdish-token']);

        const result: Api.Result<any> = response.data;

        const pageUseEndTime = performance.now();
        const elapsedTime = getElapsedTimeForAnalytics(pageUseEndTime);

        if (result.Success) {
          login();
          hashHistory.replace(afterLoginUrl);

          Track(
            'Login',
            'Guest Login Successful',
            getLoginAnalyticsData(
              elapsedTime,
              'Guest Login Successful',
              result.Success,
            ),
          );
        } else {
          isProcessingCode.current = false;
          errorMessage.current = translate('There_was_an_error_logging_in');

          Track(
            'Login',
            'Guest Login Failed',
            getLoginAnalyticsData(elapsedTime, 'Guest Login Failed'),
          );
        }
      })
      .catch((_error) => {
        // eslint-disable-next-line no-alert
        alert(translate('There_was_an_error_logging_in'));
        Track('Login', 'Guest Login Failed');
      });

    Track('Login', 'Submit Guest Login');
  };

  const handlePhonecallError = (): void => {
    errorMessage.current = translate(
      'There_was_an_error_requesting_a_phone_call',
    );
    Track('PhoneCall', 'Fallback Phone Call for SMS Code Failed');
  };

  const handlePhonecallOnSubmit = (): void => {
    isCallRequested.current = true;
    errorMessage.current = '';
  };

  const handlePhonecallSuccess = (): void => {
    Track('PhoneCall', 'Fallback Phone Call for SMS Code Successful');
  };

  const callRequestStyle = clsx(isCallRequested && classes.bottomMargin);
  const callRequestMessage = isCallRequested.current
    ? translate('you_will_receive_call_soon')
    : translate('LoginConfirm_LoginConfirmDialog_DidntGetCode');

  return (
    <Dialog
      className={classes.loginConfirmDialog}
      dialogName="login-confirm"
      onClose={(): void => handleBackLink()}
      open
      startIcon={
        <IconButton
          aria-label={translate('Back')}
          className="iconLeft"
          color="secondary"
          data-fd="dialog-back-link"
          onClick={(): void => handleBackLink()}
        >
          <ArrowLeftIcon />
        </IconButton>
      }
    >
      <Typography variant="h1">{translate('Enter_four_digit_code')}</Typography>

      <Typography variant="body2-secondary">
        {translate('Your_code_was_sent_to')} {params.phone}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          aria-label={smsCodeAriaLabel}
          autoFocus
          data-fd="sms-input"
          defaultValue=""
          fullWidth
          helperText={errorMessage.current}
          id="smsCode"
          inputProps={{
            pattern: '[0-9]*',
            type: 'number',
          }}
          inputRef={register}
          key="smsCode"
          name="smsCode"
          onChange={handleSmsNumberChange}
          status={errorMessage ? 'error' : 'default'}
        />

        <Typography className={callRequestStyle}>
          {callRequestMessage}
        </Typography>

        {allowGuestLogin && (
          <Button
            className={classes.bottomMargin}
            data-fd="guest-login"
            onClick={handleGuestLogin}
            variant="text"
          >
            {translate('login_sms_guestCheckout')}
          </Button>
        )}

        <PhoneCallButton
          onBackLink={handleBackLink}
          onError={(): void => handlePhonecallError()}
          onSubmit={handlePhonecallOnSubmit}
          onSuccess={handlePhonecallSuccess}
          phoneNumber={params.phone}
          translate={translate}
        />
      </form>
    </Dialog>
  );
}
